import React from 'react';
import { useTimeFormatter, TimeDetails } from './timeFormatter';

interface TimeDisplayProps {
  endDate: string;
  format?: 'detail' | 'short';
  children?: (
    timeDetails: TimeDetails | string,
    isExpired: boolean,
  ) => React.ReactNode;
  render?: (
    timeDetails: TimeDetails | string,
    isExpired: boolean,
  ) => React.ReactNode;
}

const formatTimeString = (timeDetails: TimeDetails): string => {
  const { days, hours, minutes, seconds } = timeDetails;
  if (days > 0) {
    return `${days}д ${hours}ч ${minutes}м`;
  }
  if (hours > 0) {
    return `${hours}ч ${minutes}м ${seconds}с`;
  }
  return `${minutes}м ${seconds}с`;
};

export const TimeDisplay: React.FC<TimeDisplayProps> = React.memo(
  ({ endDate, format = 'short', children, render }) => {
    const timeLeft = useTimeFormatter(endDate, format);
    const isExpired = typeof timeLeft === 'string';

    // Если передана функция рендера через children или render prop
    if (children || render) {
      const renderFn = children || render;
      return <>{renderFn?.(timeLeft, isExpired)}</>;
    }

    // Стандартный вывод если нет render props
    if (isExpired) {
      return <>{timeLeft}</>;
    }

    return <>{formatTimeString(timeLeft as TimeDetails)}</>;
  },
);

TimeDisplay.displayName = 'TimeDisplay';

// Хук для получения только данных без компонента
export const useTimeDisplay = (
  endDate: string,
  format: 'detail' | 'short' = 'short',
) => {
  const timeLeft = useTimeFormatter(endDate, format);
  return {
    timeLeft,
    isExpired: typeof timeLeft === 'string',
  };
};
